import { createContext, ReactNode, useContext, useState } from 'react';

/**
 * The width the user dragged the suggestions list to, if any, and a setter to
 * remember a new one. The host app supplies it, since it alone can remember
 * it for the user.
 */
export type UseSuggestionsListWidth = () => readonly [
  width: number | undefined,
  setWidth: (width: number) => void,
];

const useLocalSuggestionsListWidth: UseSuggestionsListWidth = () =>
  useState<number>();

const SuggestionsListWidthContext = createContext<UseSuggestionsListWidth>(
  useLocalSuggestionsListWidth,
);

/**
 * Injected via the `useSuggestionsListWidth` prop on the knowledge management
 * entry. The value is called as a hook, so it must stay the same for the
 * lifetime of the provider.
 */
export function SuggestionsListWidthProvider({
  value,
  children,
}: {
  value: UseSuggestionsListWidth | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <SuggestionsListWidthContext.Provider
      value={value ?? useLocalSuggestionsListWidth}
    >
      {children}
    </SuggestionsListWidthContext.Provider>
  );
}

export function useSuggestionsListWidth(): ReturnType<UseSuggestionsListWidth> {
  const useHostWidth = useContext(SuggestionsListWidthContext);
  return useHostWidth();
}
